/**
 * OutreachView - Main view for outreach items (comment tracking)
 * Table-based layout showing posts, generated comments, and status
 */

import { outreachApi, type OutreachItem, type OutreachStats, type OutreachListParams } from '../services/outreach-api';
import { OutreachSettingsModal } from './OutreachSettingsModal';
import { TestPromptModal } from './TestPromptModal';

// Platform icons/colors mapping
const PLATFORM_CONFIG: Record<string, { icon: string; color: string; bgColor: string; label: string }> = {
  twitter: {
    icon: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>`,
    color: '#1DA1F2',
    bgColor: 'rgba(29, 161, 242, 0.1)',
    label: 'Twitter'
  },
  linkedin: {
    icon: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>`,
    color: '#0077B5',
    bgColor: 'rgba(0, 119, 181, 0.1)',
    label: 'LinkedIn'
  },
  instagram: {
    icon: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>`,
    color: '#E4405F',
    bgColor: 'rgba(228, 64, 95, 0.1)',
    label: 'Instagram'
  },
  youtube: {
    icon: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>`,
    color: '#FF0000',
    bgColor: 'rgba(255, 0, 0, 0.1)',
    label: 'YouTube'
  },
  skool: {
    icon: `<span class="material-symbols-outlined" style="font-size: 16px;">school</span>`,
    color: '#6366f1',
    bgColor: 'rgba(99, 102, 241, 0.1)',
    label: 'Skool'
  },
  default: {
    icon: `<span class="material-symbols-outlined" style="font-size: 16px;">language</span>`,
    color: '#6b7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
    label: 'Other'
  }
};

// Platform keys for quick filter buttons
const PLATFORM_KEYS = ['linkedin', 'instagram', 'twitter', 'skool', 'youtube'];

export class OutreachView {
  private containerEl: HTMLElement;
  private listEl: HTMLElement;
  private items: OutreachItem[] = [];
  private stats: OutreachStats | null = null;
  private isLoading: boolean = false;
  private settingsModal: OutreachSettingsModal;
  private testPromptModal: TestPromptModal;

  // Filter state
  private filters: OutreachListParams = {
    limit: 50,
    offset: 0
  };
  private sortDirection: 'asc' | 'desc' = 'desc';

  constructor() {
    this.containerEl = document.getElementById('outreach-view')!;
    this.listEl = document.getElementById('outreach-list')!;
    this.settingsModal = new OutreachSettingsModal();
    this.testPromptModal = new TestPromptModal();

    if (this.containerEl) {
      this.render();
      this.setupEventListeners();
    }
  }

  private render(): void {
    this.containerEl.innerHTML = `
      <!-- Combined Header + Filter Bar -->
      <header class="outreach-header-bar">
        <h1 class="outreach-title">Outreach</h1>

        <div class="outreach-search">
          <span class="material-symbols-outlined">search</span>
          <input type="text" id="outreach-search" placeholder="Search...">
        </div>

        <!-- Platform quick filters - single click -->
        <div class="outreach-platform-filters" id="platform-quick-filters">
          ${PLATFORM_KEYS.map(key => {
            const cfg = PLATFORM_CONFIG[key];
            return `<button class="platform-quick-btn" data-platform="${key}" style="--platform-color: ${cfg.color}; --platform-bg: ${cfg.bgColor}" title="${cfg.label}">
              ${cfg.icon}
            </button>`;
          }).join('')}
        </div>

        <!-- Status/Quality dropdowns -->
        <div class="outreach-filters">
          <div class="outreach-filter-dropdown" id="status-filter">
            <button class="outreach-filter-btn">
              <span class="material-symbols-outlined">add_circle</span>
              <span>Status</span>
            </button>
            <div class="outreach-filter-menu hidden">
              <label><input type="checkbox" value="pending"> Pending</label>
              <label><input type="checkbox" value="posted"> Posted</label>
              <label><input type="checkbox" value="failed"> Failed</label>
            </div>
          </div>
          <div class="outreach-filter-dropdown" id="quality-filter">
            <button class="outreach-filter-btn">
              <span class="material-symbols-outlined">add_circle</span>
              <span>Quality</span>
            </button>
            <div class="outreach-filter-menu hidden">
              <label><input type="checkbox" value="good"> Good</label>
              <label><input type="checkbox" value="bad"> Bad</label>
              <label><input type="checkbox" value="neutral"> Neutral</label>
            </div>
          </div>
        </div>

        <!-- Active Filters Chips (inline) -->
        <div class="outreach-active-filters" id="outreach-chips"></div>

        <!-- Right side: Sort + Actions -->
        <div class="outreach-header-right">
          <button id="outreach-test-prompt-btn" class="outreach-icon-btn" title="Test Prompt">
            <span class="material-symbols-outlined">science</span>
          </button>
          <button id="outreach-sort-btn" class="outreach-sort-btn" title="Sort">
            <span class="material-symbols-outlined">sort</span>
          </button>
          <button id="outreach-settings-btn" class="outreach-icon-btn" title="Settings">
            <span class="material-symbols-outlined">settings</span>
          </button>
          <button id="outreach-export-btn" class="outreach-icon-btn" title="Export JSON">
            <span class="material-symbols-outlined">file_download</span>
          </button>
          <button id="outreach-stats-btn" class="outreach-icon-btn" title="View Statistics">
            <span class="material-symbols-outlined">bar_chart</span>
          </button>
        </div>
      </header>

      <!-- Table Container -->
      <div class="outreach-table-container">
        <table class="outreach-table">
          <thead>
            <tr>
              <th class="col-platform">Platform</th>
              <th class="col-author">Author</th>
              <th class="col-content">Content</th>
              <th class="col-status">Status</th>
              <th class="col-time">Time</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody id="outreach-tbody">
            <!-- Rows rendered here -->
          </tbody>
        </table>
      </div>

      <!-- Stats Modal -->
      <div id="outreach-stats-modal" class="outreach-modal hidden">
        <div class="outreach-modal-content">
          <div class="outreach-modal-header">
            <h2>Outreach Statistics</h2>
            <button class="outreach-modal-close">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="outreach-modal-body" id="outreach-stats-body">
            <!-- Stats rendered here -->
          </div>
        </div>
      </div>
    `;

    this.listEl = document.getElementById('outreach-tbody')!;
  }

  private setupEventListeners(): void {
    // Search
    const searchInput = document.getElementById('outreach-search') as HTMLInputElement;
    let searchTimeout: ReturnType<typeof setTimeout>;
    searchInput?.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.filters.search = searchInput.value.trim() || undefined;
        this.filters.offset = 0;
        this.load();
      }, 300);
    });

    // Platform quick filter buttons (single click)
    this.setupPlatformQuickFilters();

    // Filter dropdowns (status, quality)
    this.setupFilterDropdown('status-filter', 'status');
    this.setupFilterDropdown('quality-filter', 'quality');

    // Sort button
    document.getElementById('outreach-sort-btn')?.addEventListener('click', () => {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      this.updateSortButton();
      this.renderList();
    });

    // Export button
    document.getElementById('outreach-export-btn')?.addEventListener('click', () => {
      this.exportData();
    });

    // Stats button
    document.getElementById('outreach-stats-btn')?.addEventListener('click', () => {
      this.showStatsModal();
    });

    // Test Prompt button
    document.getElementById('outreach-test-prompt-btn')?.addEventListener('click', () => {
      this.testPromptModal.open();
    });

    document.getElementById('outreach-settings-btn')?.addEventListener('click', () => {
      this.settingsModal.open();
    });

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.outreach-filter-dropdown')) {
        document.querySelectorAll('.outreach-filter-menu').forEach(menu => {
          menu.classList.add('hidden');
        });
      }
    });
  }

  private setupPlatformQuickFilters(): void {
    const container = document.getElementById('platform-quick-filters');
    if (!container) return;

    container.querySelectorAll('.platform-quick-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const platform = (btn as HTMLElement).dataset.platform!;

        // Toggle: if already selected, deselect
        if (this.filters.platform === platform) {
          this.filters.platform = undefined;
          btn.classList.remove('active');
        } else {
          // Deselect others, select this one
          container.querySelectorAll('.platform-quick-btn').forEach(b => b.classList.remove('active'));
          this.filters.platform = platform;
          btn.classList.add('active');
        }

        this.filters.offset = 0;
        this.renderChips();
        this.load();
      });
    });
  }

  private setupFilterDropdown(containerId: string, filterKey: 'platform' | 'status' | 'quality'): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    const btn = container.querySelector('.outreach-filter-btn');
    const menu = container.querySelector('.outreach-filter-menu');

    // Toggle menu
    btn?.addEventListener('click', (e) => {
      e.stopPropagation();
      document.querySelectorAll('.outreach-filter-menu').forEach(m => {
        if (m !== menu) m.classList.add('hidden');
      });
      menu?.classList.toggle('hidden');
    });

    // Handle checkbox changes
    menu?.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const checked = Array.from(menu.querySelectorAll('input[type="checkbox"]:checked'))
          .map(cb => (cb as HTMLInputElement).value);

        // For single selection, use first checked or undefined
        (this.filters as Record<string, string | undefined>)[filterKey] = checked[0] || undefined;
        this.filters.offset = 0;
        this.renderChips();
        this.load();
      });
    });
  }

  private renderChips(): void {
    const chipsEl = document.getElementById('outreach-chips');
    if (!chipsEl) return;

    const chips: string[] = [];

    if (this.filters.platform) {
      chips.push(`
        <span class="outreach-chip" data-filter="platform" data-value="${this.filters.platform}">
          ${this.filters.platform}
          <button class="outreach-chip-remove"><span class="material-symbols-outlined">close</span></button>
        </span>
      `);
    }
    if (this.filters.status) {
      chips.push(`
        <span class="outreach-chip" data-filter="status" data-value="${this.filters.status}">
          ${this.filters.status}
          <button class="outreach-chip-remove"><span class="material-symbols-outlined">close</span></button>
        </span>
      `);
    }
    if (this.filters.quality) {
      chips.push(`
        <span class="outreach-chip" data-filter="quality" data-value="${this.filters.quality}">
          ${this.filters.quality}
          <button class="outreach-chip-remove"><span class="material-symbols-outlined">close</span></button>
        </span>
      `);
    }

    chipsEl.innerHTML = chips.join('');

    // Chip remove handlers
    chipsEl.querySelectorAll('.outreach-chip-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const chip = (btn as HTMLElement).closest('.outreach-chip') as HTMLElement;
        const filterKey = chip.dataset.filter as 'platform' | 'status' | 'quality';

        // Clear the filter
        (this.filters as Record<string, string | undefined>)[filterKey] = undefined;

        // If platform, deselect the quick filter button
        if (filterKey === 'platform') {
          document.querySelectorAll('.platform-quick-btn').forEach(b => b.classList.remove('active'));
        } else {
          // Uncheck the checkbox for status/quality
          const checkbox = document.querySelector(
            `#${filterKey}-filter input[value="${chip.dataset.value}"]`
          ) as HTMLInputElement;
          if (checkbox) checkbox.checked = false;
        }

        this.renderChips();
        this.load();
      });
    });
  }

  private updateSortButton(): void {
    const btn = document.getElementById('outreach-sort-btn');
    if (btn) {
      btn.innerHTML = `
        <span class="material-symbols-outlined">sort</span>
        <span>Date: ${this.sortDirection === 'desc' ? 'Newest' : 'Oldest'}</span>
      `;
    }
  }

  async load(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const response = await outreachApi.list(this.filters);
      this.items = response.items;
      this.renderList();
    } catch (error) {
      console.error('[OutreachView] Load error:', error);
      this.renderError();
    } finally {
      this.isLoading = false;
    }
  }

  private renderList(): void {
    if (!this.listEl) return;

    if (this.items.length === 0) {
      this.listEl.innerHTML = `
        <tr>
          <td colspan="6" class="outreach-empty">
            <span class="material-symbols-outlined">campaign</span>
            <h3>No outreach items yet</h3>
            <p>Generated comments will appear here</p>
          </td>
        </tr>
      `;
      return;
    }

    // Sort items
    const sorted = [...this.items].sort((a, b) => {
      return this.sortDirection === 'desc'
        ? b.created_at - a.created_at
        : a.created_at - b.created_at;
    });

    this.listEl.innerHTML = sorted.map(item => this.renderRow(item)).join('');
    this.attachRowListeners();
  }

  private renderRow(item: OutreachItem): string {
    const platform = PLATFORM_CONFIG[item.platform.toLowerCase()] || PLATFORM_CONFIG.default;
    const timeAgo = this.formatTimeAgo(new Date(item.created_at));

    // Status badge
    const statusConfig: Record<string, { class: string; label: string }> = {
      pending: { class: 'status-pending', label: 'Pending' },
      posted: { class: 'status-posted', label: 'Posted' },
      failed: { class: 'status-failed', label: 'Failed' }
    };
    const status = statusConfig[item.status] || statusConfig.pending;

    // Actions based on status
    const actionsHtml = this.renderActions(item);

    // Reasoning text (if available)
    const reasoningHtml = item.reasoning
      ? `<div class="reasoning-wrapper">
          <span class="reasoning-label">Reasoning:</span>
          <p class="reasoning-text">${this.escapeHtml(item.reasoning.slice(0, 300))}${item.reasoning.length > 300 ? '...' : ''}</p>
        </div>`
      : '';

    return `
      <tr class="outreach-row" data-id="${item.id}">
        <td class="col-platform">
          <div class="platform-icon" style="background: ${platform.bgColor}; color: ${platform.color};">
            ${platform.icon}
          </div>
        </td>
        <td class="col-author">
          <span class="author-name">${this.escapeHtml(item.post_author || 'Unknown')}</span>
        </td>
        <td class="col-content">
          <div class="content-wrapper">
            <p class="post-content">${this.escapeHtml((item.post_content || '').slice(0, 150))}${(item.post_content || '').length > 150 ? '...' : ''}</p>
            <div class="comment-wrapper">
              ${item.post_url ? `<a href="${this.escapeHtml(item.post_url)}" target="_blank" rel="noopener" class="comment-link-btn" title="Open post"><span class="material-symbols-outlined">open_in_new</span></a>` : ''}
              <div class="comment-indicator"></div>
              <p class="comment-text">${this.escapeHtml(item.comment)}</p>
            </div>
            ${reasoningHtml}
          </div>
        </td>
        <td class="col-status">
          <span class="status-badge ${status.class}">${status.label}</span>
        </td>
        <td class="col-time">
          <span class="time-text">${timeAgo}</span>
        </td>
        <td class="col-actions">
          ${actionsHtml}
        </td>
      </tr>
    `;
  }

  private renderRating(quality: string | null): string {
    if (quality === 'good') {
      return `
        <div class="rating-stars good">
          ${Array(5).fill('<span class="material-symbols-outlined filled">star</span>').join('')}
        </div>
      `;
    } else if (quality === 'bad') {
      return `
        <div class="rating-stars bad">
          <span class="material-symbols-outlined filled">star</span>
          ${Array(4).fill('<span class="material-symbols-outlined">star</span>').join('')}
        </div>
      `;
    } else if (quality === 'neutral') {
      return `
        <div class="rating-stars neutral">
          ${Array(3).fill('<span class="material-symbols-outlined filled">star</span>').join('')}
          ${Array(2).fill('<span class="material-symbols-outlined">star</span>').join('')}
        </div>
      `;
    }

    return `<div class="rating-unrated">-</div>`;
  }

  private renderActions(item: OutreachItem): string {
    // If already rated, just show delete
    if (item.quality) {
      return `
        <div class="action-buttons">
          <span class="rated-label">${item.quality}</span>
          <button class="action-btn danger" data-action="delete" title="Delete">
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      `;
    }

    return `
      <div class="action-buttons">
        <button class="action-btn good" data-action="good" title="Mark as Good">
          <span class="material-symbols-outlined">thumb_up</span>
        </button>
        <button class="action-btn bad" data-action="bad" title="Mark as Bad">
          <span class="material-symbols-outlined">thumb_down</span>
        </button>
        <button class="action-btn danger" data-action="delete" title="Delete">
          <span class="material-symbols-outlined">delete</span>
        </button>
      </div>
    `;
  }

  private attachRowListeners(): void {
    // Quality rating buttons
    this.listEl.querySelectorAll('.action-btn[data-action="good"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const row = (btn as HTMLElement).closest('.outreach-row') as HTMLElement;
        const id = row.dataset.id!;
        await this.updateQuality(id, 'good');
      });
    });

    this.listEl.querySelectorAll('.action-btn[data-action="bad"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const row = (btn as HTMLElement).closest('.outreach-row') as HTMLElement;
        const id = row.dataset.id!;
        await this.updateQuality(id, 'bad');
      });
    });

    // Delete buttons
    this.listEl.querySelectorAll('.action-btn[data-action="delete"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const row = (btn as HTMLElement).closest('.outreach-row') as HTMLElement;
        const id = row.dataset.id!;
        if (confirm('Delete this outreach item?')) {
          await this.deleteItem(id);
        }
      });
    });
  }

  private async updateQuality(id: string, quality: 'good' | 'bad' | 'neutral'): Promise<void> {
    try {
      await outreachApi.updateQuality(id, quality);
      // Update local state
      const item = this.items.find(i => i.id === id);
      if (item) {
        item.quality = quality;
        this.renderList();
      }
    } catch (error) {
      console.error('[OutreachView] Update quality error:', error);
    }
  }

  private async deleteItem(id: string): Promise<void> {
    try {
      await outreachApi.delete(id);
      this.items = this.items.filter(i => i.id !== id);
      this.renderList();
    } catch (error) {
      console.error('[OutreachView] Delete error:', error);
    }
  }

  private async exportData(): Promise<void> {
    try {
      const blobUrl = await outreachApi.exportJson(this.filters);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `outreach-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('[OutreachView] Export error:', error);
    }
  }

  private async showStatsModal(): Promise<void> {
    const modal = document.getElementById('outreach-stats-modal');
    const body = document.getElementById('outreach-stats-body');
    if (!modal || !body) return;

    body.innerHTML = '<div class="loading">Loading...</div>';
    modal.classList.remove('hidden');

    try {
      this.stats = await outreachApi.getStats();
      body.innerHTML = this.renderStats();
    } catch (error) {
      body.innerHTML = '<div class="error">Failed to load statistics</div>';
    }

    // Close handlers
    modal.querySelector('.outreach-modal-close')?.addEventListener('click', () => {
      modal.classList.add('hidden');
    });
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.add('hidden');
    });
  }

  private renderStats(): string {
    if (!this.stats) return '';

    const successRate = this.stats.total > 0
      ? Math.round((this.stats.posted / this.stats.total) * 100)
      : 0;

    return `
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">${this.stats.total}</div>
          <div class="stat-label">Total</div>
        </div>
        <div class="stat-card success">
          <div class="stat-value">${this.stats.posted}</div>
          <div class="stat-label">Posted</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-value">${this.stats.pending}</div>
          <div class="stat-label">Pending</div>
        </div>
        <div class="stat-card danger">
          <div class="stat-value">${this.stats.failed}</div>
          <div class="stat-label">Failed</div>
        </div>
      </div>

      <div class="stats-section">
        <h3>Success Rate</h3>
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${successRate}%"></div>
        </div>
        <span class="progress-label">${successRate}%</span>
      </div>

      <div class="stats-section">
        <h3>By Platform</h3>
        <div class="stats-list">
          ${this.stats.byPlatform.map(p => `
            <div class="stats-list-item">
              <span class="stats-list-label">${p.platform}</span>
              <span class="stats-list-value">${p.count}</span>
            </div>
          `).join('')}
        </div>
      </div>

      <div class="stats-section">
        <h3>By Quality</h3>
        <div class="stats-list">
          ${this.stats.byQuality.map(q => `
            <div class="stats-list-item">
              <span class="stats-list-label quality-${q.quality}">${q.quality}</span>
              <span class="stats-list-value">${q.count}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderError(): void {
    if (!this.listEl) return;
    this.listEl.innerHTML = `
      <tr>
        <td colspan="7" class="outreach-error">
          <span class="material-symbols-outlined">error</span>
          <h3>Error loading data</h3>
          <button onclick="location.reload()">Retry</button>
        </td>
      </tr>
    `;
  }

  private formatTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
